import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { createFileRoute, Link } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import {
  DownloadIcon,
  LibraryBigIcon,
  PlusIcon,
  SearchIcon,
} from "lucide-react";
import { MainTitle } from "@/components/main-title";
import { DeleteCollectionDialog } from "@/components/collection/delete-collection-dialog";
import { ImportCollectionsDialog } from "@/components/collection/import-collections-dialog";
import { RuleSummary } from "@/components/collection/rule-summary";
import { collectionStore } from "@/components/collection/sync-collection";
import { Button, buttonVariants } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import GatedOverlay from "@/components/ui/gated-overlay";
import { Input } from "@/components/ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { collectionsQueryOpts, useDeleteCollections } from "@/hooks/collection";
import { isGated } from "@/hooks/gated";
import { platformsQueryOpts } from "@/hooks/platform";
import { useDebounce } from "@/hooks/use-debounce";
import { getOrgIdWithFallback } from "@/lib/utils";
import { useAuth } from "@/providers/auth";
import { useOrg } from "@/providers/organization";

export const Route = createFileRoute("/$org/collections/")({
  loader: async ({ context }) => {
    const { getToken, setToken, handleTokenInvalid } = context.auth;
    const orgId = getOrgIdWithFallback(context.org.orgId);
    const params = { token: getToken() ?? "", setToken, orgId };

    const gated = await isGated(params, handleTokenInvalid);

    context.queryClient.ensureQueryData(collectionsQueryOpts(params));
    context.queryClient.ensureQueryData(platformsQueryOpts(params));

    return { gated: gated?.is_gated };
  },
  component: CollectionsComponent,
});

function CollectionsComponent() {
  const { t } = useTranslation();
  const { gated } = Route.useLoaderData();
  const { org } = Route.useParams();
  const { getToken, setToken } = useAuth();
  const { orgId } = useOrg();
  const params = { token: getToken() ?? "", setToken, orgId };

  const [search, setSearch] = useState("");
  const debouncedSearch = useDebounce(search, 300);

  const { data: collections, isPending } = useQuery({
    ...collectionsQueryOpts(params, debouncedSearch),
    placeholderData: (previousData) => previousData,
  });
  const { data: stores } = useQuery(platformsQueryOpts(params));
  const shopifyStores = (stores ?? []).filter(
    (store) => store.platform === "shopify",
  );
  const [importing, setImporting] = useState(false);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [deleting, setDeleting] = useState(false);
  const deleteCollections = useDeleteCollections();

  if (gated) {
    return <GatedOverlay org={org} />;
  }

  const list = collections ?? [];
  const searching = debouncedSearch.trim() !== "";

  // only the listed collections stay selected, e.g. after a new search
  const selectedIds = list.filter((c) => selected.has(c.id)).map((c) => c.id);
  const allSelected = list.length > 0 && selectedIds.length === list.length;

  const toggle = (id: string, checked: boolean) =>
    setSelected((current) => {
      const next = new Set(current);
      if (checked) next.add(id);
      else next.delete(id);
      return next;
    });

  const toggleAll = (checked: boolean) =>
    setSelected(checked ? new Set(list.map((c) => c.id)) : new Set());

  const deleteSelected = () =>
    deleteCollections.mutate(selectedIds, {
      onSuccess: () => {
        setSelected(new Set());
        setDeleting(false);
      },
    });

  return (
    <div className="flex flex-col">
      <MainTitle
        title={t("collections.title")}
        description={t("collections.description")}
      />

      <div className="flex flex-col gap-4 py-2">
        <div className="flex items-center justify-between gap-2">
          <div className="relative w-full max-w-sm">
            <SearchIcon className="absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              className="pl-9"
              placeholder={t("collections.search")}
              value={search}
              onChange={(event) => setSearch(event.target.value)}
            />
          </div>
          <div className="flex items-center gap-2">
            {shopifyStores.length > 0 && (
              <Button variant="outline" onClick={() => setImporting(true)}>
                <DownloadIcon />
                {t("collections.import.button")}
              </Button>
            )}
            <Link
              to="/$org/collections/new"
              params={{ org }}
              className={buttonVariants()}
            >
              <PlusIcon />
              {t("collections.create")}
            </Link>
          </div>
        </div>

        {list.length === 0 && !isPending ? (
          <div className="flex flex-col items-center gap-2 rounded-sm border border-dashed p-12 text-center">
            <LibraryBigIcon className="size-8 text-muted-foreground" />
            <p className="font-medium">
              {searching ? t("collections.noResults") : t("collections.empty")}
            </p>
            {!searching && (
              <p className="text-sm text-muted-foreground">
                {t("collections.emptyBody")}
              </p>
            )}
          </div>
        ) : (
          <div className="rounded-sm border">
            {selectedIds.length > 0 && (
              <div className="flex items-center gap-2 border-b bg-muted/40 px-3 py-2">
                <span className="text-sm font-medium">
                  {t("import.selectedCount", { count: selectedIds.length })}
                </span>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => setSelected(new Set())}
                >
                  {t("import.unselectAll")}
                </Button>
                <Button
                  size="sm"
                  variant="destructive"
                  className="ml-auto"
                  onClick={() => setDeleting(true)}
                >
                  {t("common.delete")}
                </Button>
              </div>
            )}
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-10">
                    <Checkbox
                      checked={
                        allSelected ||
                        (selectedIds.length > 0 && "indeterminate")
                      }
                      onCheckedChange={(checked) => toggleAll(checked === true)}
                      aria-label={t("table.selectAll")}
                    />
                  </TableHead>
                  <TableHead>{t("collections.columns.title")}</TableHead>
                  <TableHead className="text-right">
                    {t("collections.columns.products")}
                  </TableHead>
                  <TableHead>{t("collections.columns.conditions")}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {list.map((collection) => (
                  <TableRow
                    key={collection.id}
                    data-state={
                      selected.has(collection.id) ? "selected" : undefined
                    }
                  >
                    <TableCell>
                      <Checkbox
                        checked={selected.has(collection.id)}
                        onCheckedChange={(checked) =>
                          toggle(collection.id, checked === true)
                        }
                        aria-label={t("table.selectRow")}
                      />
                    </TableCell>
                    <TableCell className="font-medium">
                      <Link
                        to="/$org/collections/$id"
                        params={{ org, id: collection.id }}
                        className="hover:underline"
                      >
                        {collection.title}
                      </Link>
                      {collection.platform_connection_id && (
                        <p className="text-xs font-normal text-muted-foreground">
                          {t("collections.importedFrom", {
                            store:
                              collectionStore(collection, stores ?? [])
                                ?.alias ?? "Shopify",
                          })}
                        </p>
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      {collection.products_count}
                    </TableCell>
                    <TableCell>
                      <RuleSummary
                        collection={collection}
                        stores={stores ?? []}
                      />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </div>

      <ImportCollectionsDialog
        open={importing}
        onOpenChange={setImporting}
        stores={shopifyStores}
      />

      <DeleteCollectionDialog
        open={deleting}
        onOpenChange={setDeleting}
        count={selectedIds.length}
        onConfirm={deleteSelected}
        isPending={deleteCollections.isPending}
      />
    </div>
  );
}
